import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ApolloProvider } from 'react-apollo';
import { Logo } from 'donor/portal/pages/Routes';
import { COLORS } from '../../design-system/settings/variables/Colors';
import { useGraphQLRequest } from '../hooks/useApiRequest';
import {
  PublicAnswersAndResultsResponse,
  PublicReportData,
  getPublicAnswersAndResults,
  getPublicReport,
  getPublicServices,
} from './PublicReportApi';
import { createClient, useClient } from '../../apollo';
import {
  answersBySection,
  ReportAnswers,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackReport';
import { useLocation } from 'react-router-dom';
import TrueImpactLoading from '../../components/TrueImpactLoading';
import TrueImpactText from '../../design-system/base/TrueImpactText';
import { SPACERS } from '../../design-system/settings/variables/Spacers';
import { useQueryString } from '../hooks/useQueryString';
import { isCnUserView } from '../../utils/partnerUser';
import { FeedbackDashboardCardProps } from '../../components/Feedback/FeedbackDashboardCard';
import {
  feedbackMapFromApi,
  getFeedback,
} from 'donor/portal/pages/FeedbackDashboard/api';
import { useIr } from 'shared/apollo/pageHooks/useIr';
import FeedbackImpactReceipt from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt';
import { ImpactReceipt } from 'donor/portal/pages/DonorReceipt/utils';
import toCamelCase from 'camelcase-keys';
import {
  PublicServicesProvider,
  PublicService,
} from '../../providers/PublicServicesContext';
import {
  IndicatorProvider,
  useIndicator,
} from '../../providers/IndicatorProvider';
import { processReportResults } from '../../providers/IndicatorProvider/utils';

const StyledDiv = styled.div`
  .header {
    background-color: ${COLORS.reportBuilderGray};
  }
  .logo {
    position: unset;
  }
  .error-message {
    text-align: center;
    padding: ${SPACERS.large};
  }
  .impact-receipt {
    .impact-receipt-report-header {
    }
    .impact-receipt-report-content {
      margin: 0 auto;
      max-width: 900px;
    }
  }
`;

const Header = (): JSX.Element => {
  return (
    <div className="header">
      <Logo className="logo" />
    </div>
  );
};

const Error = (): JSX.Element => {
  return (
    <StyledDiv>
      <Header />
      <TrueImpactText color="critical" className="error-message">
        Unable to load the report.
      </TrueImpactText>
    </StyledDiv>
  );
};

const Loading = (): JSX.Element => {
  return (
    <StyledDiv>
      <Header />
      <TrueImpactLoading />
    </StyledDiv>
  );
};

const PublicReportContent: React.FC<{
  isCnAuth0User: boolean;
  feedback: FeedbackDashboardCardProps;
  reportId: string;
  answers: ReportAnswers;
  ir: ImpactReceipt | undefined;
}> = ({ isCnAuth0User, feedback, reportId, answers, ir }) => {
  const { loadReportIndicators, isLoading } = useIndicator();

  useEffect(() => {
    loadReportIndicators(reportId);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <StyledDiv>
      <Header />
      <div className="impact-receipt">
        <FeedbackImpactReceipt
          isThirdParty={isCnAuth0User}
          programName={feedback.title || feedback.portfolioName}
          reportId={reportId}
          answers={answers}
          feedback={feedback}
          isLegacy={ir?.report?.isLegacy}
          report={ir?.report}
          ir={ir}
        />
      </div>
    </StyledDiv>
  );
};

const PublicReport: React.FC = () => {
  const authClient = useClient();
  const query = useQueryString();
  const token = query.get('token');
  const { pathname } = useLocation();
  const isCnAuth0User = isCnUserView();
  const [publicClient] = useState(() => createClient({}));
  const [
    publicAnswersAndResultsState,
    makePublicAnswersAndResultsRequest,
  ] = useGraphQLRequest<PublicAnswersAndResultsResponse>();
  const [reportState, makeReportRequest] = useGraphQLRequest<{
    publicReport: PublicReportData;
  }>();
  const [servicesState, makeServicesRequest] = useGraphQLRequest<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    publicReportServices: any[];
  }>();
  const [feedbackRequestState, makeFeedbackRequest] = useGraphQLRequest<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reportsTodo: any[];
  }>();
  const [feedback, setFeedback] = useState<FeedbackDashboardCardProps>();
  const [allocationId, setAllocationId] = useState<number>();
  const [investmentId, setInvestmentId] = useState<number>();
  const reportIdRef = useRef<string | null>(
    pathname.split('/report/').pop() || null,
  );
  const reportId = reportIdRef.current;

  const client = token ? publicClient : authClient;

  // Only fetch IR data when investmentId is available
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: irData } = useIr(investmentId as any, allocationId as any);

  const ir: ImpactReceipt | undefined =
    irData && Object.keys(irData).length > 0
      ? toCamelCase(irData, { deep: true })
      : undefined;

  useEffect(() => {
    if (!client || !reportId) return;

    if (token) {
      makePublicAnswersAndResultsRequest(
        getPublicAnswersAndResults(client, reportId, token),
      );
      makeReportRequest(getPublicReport(client, reportId, token));
      makeServicesRequest(getPublicServices(client, reportId, token));
    } else if (isCnAuth0User || authClient) {
      makePublicAnswersAndResultsRequest(
        getPublicAnswersAndResults(client, reportId, ''),
      );
    }
  }, [client, reportId, token, isCnAuth0User]);

  // Only fetch feedback via authenticated endpoint when NOT using public token
  useEffect(() => {
    if (token) return; // Skip for public access - we use publicReport query instead
    client &&
      reportId &&
      makeFeedbackRequest(
        getFeedback(
          client,
          undefined,
          undefined,
          reportId,
          undefined,
          undefined,
        ),
      );
  }, [client, reportId, token]);

  // Set feedback from publicReport query when using public token
  useEffect(() => {
    if (
      token &&
      reportState.type === 'REQUEST_SUCCESS' &&
      reportState.data?.publicReport
    ) {
      const rpt = reportState.data.publicReport;
      // Create a minimal feedback object for public access
      setFeedback(({
        title: rpt.name,
        portfolioName: rpt.portfolioName,
        programName: rpt.programName,
        status: rpt.status,
        stage: rpt.stage,
        year: rpt.year,
        npoName: rpt.npoName,
        donorName: rpt.donorName,
      } as unknown) as FeedbackDashboardCardProps);
    }
  }, [token, reportState.type, reportState.data]);

  // Set feedback from authenticated getFeedback when NOT using public token
  useEffect(() => {
    if (token) return; // Skip for public access
    if (feedbackRequestState.type === 'REQUEST_SUCCESS') {
      const feedbacks = feedbackMapFromApi(
        feedbackRequestState.data.reportsTodo,
      ).feedbackRows;
      setFeedback(feedbacks[0]);
      // Safely Detects the allocation ID and the investment ID, for V3 Reports
      try {
        const rawAllocationId =
          feedbackRequestState.data.reportsTodo?.[0]?.allocations?.[0]?.dbId;
        const rawInvestmentId =
          feedbackRequestState.data.reportsTodo?.[0]?.allocations?.[0]
            ?.investment?.dbId;
        setAllocationId(rawAllocationId);
        setInvestmentId(rawInvestmentId);
      } catch (error) {
        console.error(error);
      }
    }
  }, [token, feedbackRequestState.type]);

  const { answers } = useMemo(() => {
    const answers = answersBySection(
      publicAnswersAndResultsState.data?.allNpoReportAnswers || [],
    );
    const impactModel = processReportResults(
      publicAnswersAndResultsState.data?.publicReportResults,
      answers,
    );
    return { answers, impactModel };
  }, [publicAnswersAndResultsState]);

  // Transform services data for PublicServicesProvider
  const publicServices: PublicService[] = useMemo(() => {
    if (
      servicesState.type !== 'REQUEST_SUCCESS' ||
      !servicesState.data?.publicReportServices
    ) {
      return [];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return servicesState.data.publicReportServices.map((service: any) => ({
      dbId: service.dbId,
      name: service.name,
      description: service.description,
      goal: service.goal,
      version: service.version,
      indicators:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        service.indicators?.edges?.map((edge: any) => edge.node) || [],
    }));
  }, [servicesState]);

  if (!client || !reportId || (!token && !isCnAuth0User && !authClient)) {
    return <Error />;
  }

  // Loading state - only check reportState when using public token
  const isLoading =
    publicAnswersAndResultsState.type === 'REQUEST_INIT' ||
    publicAnswersAndResultsState.type === 'REQUEST_START' ||
    (token &&
      (reportState.type === 'REQUEST_INIT' ||
        reportState.type === 'REQUEST_START'));

  if (isLoading) {
    return <Loading />;
  }

  if (
    !feedback ||
    publicAnswersAndResultsState.type === 'REQUEST_ERROR' ||
    (token && reportState.type === 'REQUEST_ERROR')
  ) {
    return <Error />;
  }

  return (
    <ApolloProvider client={client}>
      <PublicServicesProvider
        services={publicServices}
        loading={servicesState.type === 'REQUEST_START'}
      >
        <IndicatorProvider publicToken={token || undefined}>
          <PublicReportContent
            ir={ir}
            answers={answers}
            reportId={reportId}
            feedback={feedback}
            isCnAuth0User={isCnAuth0User}
          />
        </IndicatorProvider>
      </PublicServicesProvider>
    </ApolloProvider>
  );
};

export default PublicReport;
